import React, { ReactElement, useRef, KeyboardEvent, useMemo, ClipboardEvent } from 'react';
import { Box, InputProps, Input, FormHelperText, Theme, SxProps } from '@mui/material';
import { v4 } from 'uuid';

const styles = {
  input: {
    mx: 0.5,
    fontSize: 24,
    '& > input': {
      textAlign: 'center',
    },
  },
  helperText: {
    mt: 1,
  },
} as const;

enum KeyCodes {
  backspace = 8,
  left = 37,
  up = 38,
  right = 39,
  down = 40,
  home = 36,
  end = 35,
}

export interface ICodeFieldProps {
  value: string;
  error?: string;
  digits?: number;
  disabled?: boolean;
  onChange: (value: string | null) => void;
  FieldProps?: InputProps;
  sx?: SxProps<Theme>;
}

const getCodeFromValues = (value: string, digits: number = 6) => [...Array(digits)].map((val, idx) => value[idx] ?? '');

const VerificationCodeField = ({
  value,
  digits = 6,
  onChange,
  disabled = false,
  error,
  FieldProps,
  sx = [],
}: ICodeFieldProps): ReactElement => {
  const inputs = useRef<(HTMLDivElement | null)[]>([]);
  const indexes = useMemo(() => [...Array(digits)].map(() => v4()), [digits]);

  const processChange = (num: string, idx: number) => {
    if (/[^0-9]/.test(num)) {
      return;
    }

    const newCode = getCodeFromValues(value, digits);
    newCode[idx] = num;
    if (idx !== digits - 1) {
      inputs.current[idx + 1]?.focus();
    }

    onChange(newCode.join(''));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>, idx: number) => {
    const prevIndex = idx >= 1 ? idx - 1 : 0;
    const nextIndex = idx + 1;
    const prev = inputs.current[prevIndex];
    const next = inputs.current[nextIndex];

    const isBackspace = e.keyCode === KeyCodes.backspace;
    const isLeft = e.keyCode === KeyCodes.left;
    const isRight = e.keyCode === KeyCodes.right;
    const isHome = e.keyCode === KeyCodes.home;
    const isEnd = e.keyCode === KeyCodes.end;

    if (isBackspace) {
      e.preventDefault();
      onChange(value.substring(0, idx));
      if (prev) {
        prev.focus();
      }
      return;
    }

    if (isLeft && prev) {
      e.preventDefault();
      prev.focus();
      return;
    }

    if (isRight && next) {
      e.preventDefault();
      next.focus();
      return;
    }

    if (isHome) {
      e.preventDefault();
      inputs.current[0]?.focus();
      return;
    }

    if (isEnd) {
      e.preventDefault();
      inputs.current[digits - 1]?.focus();
    }

    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
      return;
    }

    e.preventDefault();
    processChange(e.key, idx);
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const { clipboardData } = e;
    const pastedData = clipboardData.getData('text/plain').substr(0, digits);
    const parsedNumber = parseInt(pastedData, 10);
    if (!Number.isNaN(parsedNumber) && Number.isInteger(parsedNumber)) {
      const copiedValue = parsedNumber.toString();
      onChange(copiedValue);
      const caretPosition = copiedValue.length >= digits ? digits - 1 : copiedValue.length;
      inputs.current[caretPosition]?.focus();
    }
  };

  const code = getCodeFromValues(value, digits);

  return (
    <Box sx={sx}>
      <Box display="flex" alignItems="center" justifyContent="center">
        {code.map((num, idx) => (
          <Input
            key={indexes[idx]}
            sx={styles.input}
            type="text"
            value={num}
            error={!!error}
            disabled={disabled}
            onKeyDown={e => handleKeyDown(e, idx)}
            onPaste={handlePaste}
            componentsProps={{
              input: {
                maxLength: 1,
                inputMode: 'numeric',
              },
            }}
            inputRef={ref => {
              if (!inputs.current[idx]) {
                inputs.current.push(ref);
              }
            }}
            autoFocus={!code[0].length && idx === 0}
            {...FieldProps}
          />
        ))}
      </Box>
      {!!error && (
        <FormHelperText sx={styles.helperText} error={!!error}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
};

export default VerificationCodeField;
